import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtNum } from "../lib/api";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function LinkChannel() {
  const [platform, setPlatform] = useState("kick");
  const [handle, setHandle] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [refundAccepted, setRefundAccepted] = useState(false);
  const [reqs, setReqs] = useState({ min_followers: 100, min_videos: 5 });
  const { t } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/config/requirements").then(({ data }) => setReqs(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/channels/link", { platform, handle, refund_policy_accepted: refundAccepted });
      setResult(data);
      if (data.verified) toast.success(t("link_ok_toast"));
      else toast.error(t("link_fail_toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="link-channel-page" className="max-w-2xl mx-auto px-6 py-16">
      <h1 className="text-4xl font-black tracking-tighter text-white mb-2">{t("link_title")}</h1>
      <p className="text-sm text-zinc-400 mb-10">
        {t("link_sub", { f: reqs.min_followers, v: reqs.min_videos })}
      </p>
      <form onSubmit={submit} className="bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8 space-y-6">
        <div className="grid grid-cols-2 gap-3">
          {[
            ["kick", "Kick", "border-emerald-500 bg-emerald-600/15 text-emerald-400"],
            ["youtube", "YouTube", "border-red-500 bg-red-600/15 text-red-400"],
          ].map(([id, label, activeCls]) => (
            <button key={id} type="button" data-testid={`platform-${id}-btn`} onClick={() => setPlatform(id)}
              className={`rounded-xl border p-4 font-semibold transition-colors text-sm ${platform === id ? activeCls : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">{t("link_handle")}</Label>
          <Input data-testid="channel-handle-input" required value={handle}
            onChange={(e) => setHandle(e.target.value)}
            className="bg-zinc-950 border-zinc-800 text-white" placeholder={platform === "kick" ? "ej: xqc" : "ej: @MrBeast"} />
        </div>
        <div data-testid="refund-policy-box" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-emerald-400 mb-2">{t("refund_policy_title")}</p>
          <p className="text-xs text-zinc-400 leading-relaxed mb-3">{t("refund_policy_text")}</p>
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input data-testid="refund-policy-checkbox" type="checkbox" checked={refundAccepted}
              onChange={(e) => setRefundAccepted(e.target.checked)}
              className="h-4 w-4 mt-0.5 accent-emerald-500 shrink-0" />
            <span className="text-xs text-zinc-300">{t("refund_policy_accept")}</span>
          </label>
        </div>
        <Button data-testid="link-channel-submit-btn" type="submit" disabled={loading || !refundAccepted}
          className="w-full rounded-full bg-emerald-600 hover:bg-emerald-500 text-white h-11 disabled:opacity-40">
          {loading ? t("link_verifying") : t("link_submit")}
        </Button>
      </form>

      {result && (
        <div data-testid="verification-result" className={`mt-8 rounded-2xl border p-8 ${result.verified ? "border-emerald-700/50 bg-emerald-950/30" : "border-red-800/50 bg-red-950/20"}`}>
          <div className="flex items-center gap-3 mb-6">
            {result.verified ? <CheckCircle2 className="text-emerald-400" size={28} /> : <XCircle className="text-red-400" size={28} />}
            <h3 className="text-2xl font-semibold text-white">
              {result.verified ? t("link_ok") : t("link_fail")}
            </h3>
          </div>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-300">{t("link_channel")} <span className="font-semibold text-white">{result.display_name}</span> ({result.platform})</p>
            <div className="flex items-center gap-2">
              {result.requirements.followers_ok ? <CheckCircle2 size={16} className="text-emerald-400" /> : <XCircle size={16} className="text-red-400" />}
              <span className="text-zinc-300">{t("link_followers_line", { a: fmtNum(result.followers), b: result.requirements.min_followers })}</span>
            </div>
            <div className="flex items-center gap-2">
              {result.requirements.videos_ok ? <CheckCircle2 size={16} className="text-emerald-400" /> : <XCircle size={16} className="text-red-400" />}
              <span className="text-zinc-300">{t("link_videos_line", { a: fmtNum(result.videos), b: result.requirements.min_videos })}</span>
            </div>
            {result.data_source === "mock" && (
              <p className="text-xs text-amber-400/80 mt-2">{t("link_mock")}</p>
            )}
          </div>
          {result.verified && (
            <Button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")}
              className="mt-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold">
              {t("link_go_dash")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
